import React from "react";
import appConstants from "../../../../appConstants";
import {
  getMinutesfromTimeString,
  getUTCDate,
} from '@dataGrid/gridUtilities';
import { ALL_DRIVER_STATUS, SHOW_DRIVER_EVENTS } from '../RouteExecution/constants';
import { format, differenceInMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { downloadBlobDataByURLs } from "../../../common/ProImages/components/utils/blobUtils";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import { isDriverTimeBeforeCurrentDateAndTime } from "../RouteExecution/utils";
import { PICKUPS_GRID_FILTERS } from "../RouteExecution/Pickups/Grid/Filter/DEFAULT_constants";
import { isNotAssignedPickup, isNotCompletedPickup } from "../RouteExecution/filterUtils";
import { getMinutesInHourAndMinutesStr } from "../RouteExecution/DriverPopUp/common/utils";
import { CUSTOMER_CONFIG } from "../RouteExecution/DriverPopUp/common/constants";
import ToolTip from '../../../common/StyledToolTip';
import { 
  splitByDelimiter, 
  joinByDelimiter,
  TimeRangeDelimiter,
} from '../../../common/utils/stringUtils';
import { RMAXLogger } from "../../../../services/LoggerService/RMAXLogger";
import { getDisplayServiceLevels } from "../../shared/serviceLevelUtils";
import { getPickupNumberIdListFromPickup } from "../RouteExecution/Pickups/GroupPickup/utils";

const formatPhone = (phone) => {
  if (!phone) {
    return null;
  }

  let prefix = "";
  if(phone.startsWith("+")){
    prefix = phone.slice(0, -10) + " ";
    phone = phone.slice(-10)
  }

  const SPECIAL_CHAR_CHECK = /[-\/\\#,+()"]/g;
  if (!SPECIAL_CHAR_CHECK.test(phone)) {
    return phone.length === 10
      ? prefix  + phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
      : "";
  } else {
    return prefix + phone;
  }
};

const formatNumber = (number) => {
  if (!number) {
    return "";
  }
  return new Intl.NumberFormat(appConstants.LOCALES).format(number);
}

const filterAndJoin = (fieldList, delimiter) => {
  return fieldList.filter(Boolean).join(delimiter);
};
const formatAddress = ({ address, address2, city, state, zip, country }) => {
  return `${filterAndJoin([address, address2, city, state, country], ", ")} ${
    zip ?? ""
  }`.trim();
}; //todo - this function we can moved globally

const formatPickupServiceWindow = (readyTime, closeTime) => {
  return `${readyTime ?? ""} - ${closeTime ?? ""}`;
};

const isDeliveryStop = (stopType) => {
  return stopType === appConstants.STOP_TYPE.Delivery;
};

const normalizeDeliveryWindow = (deliveryWindow, fallbackValue = []) => {
  // Handle non-array or falsy deliveryWindow
  if (!Array.isArray(deliveryWindow)) {
    RMAXLogger.warn('Invalid deliveryWindow format in normalizeDeliveryWindow', {
      windowType: typeof deliveryWindow,
      error: 'Expected array of strings'
    });
    return [...fallbackValue];
  }

  // Filter and log invalid element types
  return deliveryWindow.filter(window => {
    const isStr = typeof window === 'string';
    if (!isStr) {
      RMAXLogger.warn('Invalid deliveryWindow data type in normalizeDeliveryWindow', {
        windowType: typeof window,
        windowCount: deliveryWindow.length,
        error: `Expected string but received ${typeof window}`
      });
    }
    return isStr;
  });
};


const formatDeliveryOrServiceWindow = (windows, emptyFallback = "") => {
  return normalizeDeliveryWindow(windows)?.length > 0 
    ? joinByDelimiter(windows) 
    : emptyFallback;
};

const getFormattedDeliveryWindow = (deliveryWindow) => {
  const normalizedDeliveryWindows = normalizeDeliveryWindow(deliveryWindow)
    .map(window => window.replaceAll(' ', ''));
  return formatDeliveryOrServiceWindow(normalizedDeliveryWindows);
};

const getStopServiceWindowByStopType = (stopData) => {
  if (isDeliveryStop(stopData?.stopType)) {
    return getFormattedDeliveryWindow(stopData.deliveryWindow);
  }
  return formatPickupServiceWindow(stopData?.readyTime, stopData?.closeTime);
};

const getDriverStopStatus = (isOnTime) => {
  return isOnTime ? appConstants.STOP_STATUS.ONTIME : appConstants.STOP_STATUS.DELAYED;
};

const checkIfETAInDeliveryRange = (eta, deliveryWindow) => {
  return normalizeDeliveryWindow(deliveryWindow).some(window => {
    const maxDeliveryTime = getMinutesfromTimeString(splitByDelimiter(window, TimeRangeDelimiter)[1]);
    return eta <= maxDeliveryTime;
  })
}

const getPendingDeliveryStatus = (stopData, driverActualArrivedTime = "") => {
  const eta = getMinutesfromTimeString(
    driverActualArrivedTime || stopData?.arrivalTime
  );
  // check if the eta is within the delivery window
  const isOnTime = checkIfETAInDeliveryRange(eta, stopData.deliveryWindow);
  return getDriverStopStatus(isOnTime);
};

const getPendingPickupStatus = (stopData, driverActualArrivedTime = "") => {
  const serviceWindow = formatPickupServiceWindow(stopData?.readyTime, stopData?.closeTime);
  if (!serviceWindow) return null;
  const maxDeliveryTime = getMinutesfromTimeString(stopData?.closeTime);
  const eta = getMinutesfromTimeString(
    driverActualArrivedTime || stopData?.arrivalTime
  );
  const isOnTime = maxDeliveryTime >= eta;
  return getDriverStopStatus(isOnTime);
};

const getPendingStopStatus = (stopData, driverActualArrivedTime = '') => {
  return isDeliveryStop(stopData?.stopType)
    ? getPendingDeliveryStatus(stopData, driverActualArrivedTime)
    : getPendingPickupStatus(stopData, driverActualArrivedTime);
};

const getAptComments = ({ data }) => {
  return (
    data.shipments
      ?.filter((shipment) => shipment.aptComments)
      ?.map((shipment) => shipment.aptComments)
      ?.join(', ') || null
  );
};

const getServiceLevel = (data, serviceLevelList) => {
  const serviceLevelArr = [];
  if (data.isApt) {
    const aptComments = getAptComments({data});
    if (aptComments) {
      serviceLevelArr.push(
        <ToolTip title={aptComments}>
          <span>{appConstants.SpecialNeedsStrings.APT}</span>
        </ToolTip>
      );
    } else {
      serviceLevelArr.push(appConstants.SpecialNeedsStrings.APT);
    }
  }
  data.priority?.length &&
    serviceLevelArr.push(...getDisplayServiceLevels(data.priority, serviceLevelList));
  return serviceLevelArr.reduce((prev, curr, index) => index === 0 ? [curr]: [...prev, ', ', curr], []);
};

const getDeliveryStatusFromShipmentDelCode = (delCode) => {
  switch (delCode) {
    case appConstants.DELIVERY_CODE.RTN:
      return appConstants.DELIVERY_STATUS.RETURN;
    case appConstants.DELIVERY_CODE.DE:
      return appConstants.DELIVERY_STATUS.DELIVERY_EXCEPTION;
    default:
      return appConstants.DELIVERY_STATUS.COMPLETED;
  }
};

const getStopDeliveryStatusFromShipmentsDelCode = (shipments) => {
  if (shipments?.length === 1) {
    return getDeliveryStatusFromShipmentDelCode(shipments[0].deliveryCode);
  }
  const deliveryCodes = [...new Set(shipments.map((shipment) => shipment.deliveryCode))];
  if (deliveryCodes.length === 1) {
    if (deliveryCodes[0] === appConstants.DELIVERY_CODE.RTN)
      return appConstants.DELIVERY_STATUS.RETURN;
  }
  return appConstants.DELIVERY_STATUS.COMPLETED;
};

const sortStopsBySequence = (stops) => {
  return stops?.slice()?.sort(
    (stop, nextStop) => stop.stopSequence - nextStop.stopSequence
  )||[];
};

const getPendingStops = (stops) => {
  return sortStopsBySequence(stops).filter((stop) => !stop.isStopCompleted);
};

const isDriverCurrentStopPresent = ({ currentStatus, stops }) => {
  return stops?.some((stop) => stop?.stopId === currentStatus?.stopId);
};
const getCurrentDriverStatus = ({ currentStatus, stops }) => {
  const stopData = getCurrentStop(currentStatus, stops);
  return stopData
    ? `${SHOW_DRIVER_EVENTS[currentStatus.status]} ${stopData?.displayCustomerName}`
    : "";
};

const getCurrentStop = ( currentStatus, stops ) => {
  return stops?.find((stop) => stop?.stopId === currentStatus?.stopId)
}

const completedVsTotalStopsFormatter = (routeData) => {
  const routeStops = sortStopsBySequence(routeData.stops);
  return `${routeStops.filter((stop) => stop.isStopCompleted).length}/${
    routeStops.length
  }`;
};

const getTotalInTrailer = (stops, field) => {
  // Exclude incomplete pickup and completed delivery and isMovedOutshipment from the total weight, total hu, and total pieces calculation
  // Include return delivery shipment value
  return [
    ...getCompletedPickupShipment(stops),
    ...getPendingAndReturnDeliveryShipment(stops),
  ].reduce(
    (total, shipment) =>
      shipment.isShipmentMovedOut ? total : total + shipment[field],
    0
  );
};

// Partial driver refresh: reuse route color when routeId matches; otherwise assign from palette (e.g. first route after trailer hook).
const getDispatchRouteColor = ({
  previousRoutes,
  routeId,
  routeColorList,
  colorIndex,
}) => {
  const previousRoute = previousRoutes.find((previousRoute) => previousRoute.routeId === routeId);
  return previousRoute ? previousRoute.color: routeColorList[colorIndex % routeColorList.length];
};

const getCompletedPickupShipment = (stops) => {
  return stops.reduce((shipmentList, stop) => {
    return stop.stopType === appConstants.STOP_TYPE.Pickup &&
      stop.isStopCompleted
      ? [...stop.shipments, ...shipmentList]
      : shipmentList;
  }, []);
};

const getPendingAndReturnDeliveryShipment = (stops) => {
  return stops.reduce((shipmentList, stop) => {
    return stop.stopType === appConstants.STOP_TYPE.Delivery
      ? stop.isStopCompleted
        ? [
            ...stop.shipments.filter(
              (shipment) =>
                shipment.deliveryCode === appConstants.DELIVERY_CODE.RTN
            ),
            ...shipmentList,
          ]
        : [...stop.shipments, ...shipmentList]
      : shipmentList;
  }, []);
};

const getTotalReturnDeliveryShipments = (stopsList) => {
  const deliveryStops = stopsList.filter(
    (stop) => stop.stopType === appConstants.STOP_TYPE.Delivery
  );
  return deliveryStops.reduce(
    (acc, stop) =>
      acc + stop.shipments.filter((shipment) => shipment.returnCode).length,
    0
  );
};

const getTimeDifferenceInMinutes = (startDate, endDate) => {
  return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
};

const convertMinutesintoHours = (minutes) => {
  if (minutes >= 60) {
    return Math.floor(minutes / 60) + " hr " + (minutes % 60) + " mins";
  } else {
    return minutes + " mins";
  }
};

const formatElapsedSecondsForDisplay = (seconds) => {
  if (seconds < 1) return "just now";
  if (seconds < 60) return `${seconds} sec ago`;
  const mins = Math.floor(seconds / 60);
  return convertMinutesintoHours(mins) + " ago";
};

// This function calculates total delivery stops shipments count
const getTotalDeliveryShipments = (stops) => {
  const deliveryStops = stops.filter(
    (stop) => stop.stopType === appConstants.STOP_TYPE.Delivery
  );
  return deliveryStops.reduce((acc, stop) => acc + stop.shipments.length, 0);
};

// This function calculates pending delivery stops shipments count
const getPendingDeliveryShipments = (stopsList) => {
  const deliveryStops = stopsList.filter(
    (stop) =>
      stop.stopType === appConstants.STOP_TYPE.Delivery && !stop.isStopCompleted
  );
  return deliveryStops.reduce((acc, stop) => acc + stop.shipments.length, 0);
};

// This function calculates completed delivery stops shipments count
const getCompletedDeliveryShipments = (stopsList) => {
  const deliveryStops = stopsList.filter(
    (stop) =>
      stop.stopType === appConstants.STOP_TYPE.Delivery && stop.isStopCompleted
  );
  return deliveryStops.reduce(
    (acc, stop) =>
      acc + stop.shipments.filter((shipment) => !shipment.returnCode).length,
    0
  );
};

// This function calculate total delivery stops count
const getTotalDeliveryStops = (stops) => {
  return stops.reduce((acc, stop) => {
    if (stop.stopType === appConstants.STOP_TYPE.Delivery) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

// This function calculates the completed delivery stops count
const getTotalCompletedDeliveryStops = (stopsList) => {
  return stopsList.reduce((acc, stop) => {
    if (
      stop.stopType === appConstants.STOP_TYPE.Delivery &&
      stop.isStopCompleted
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

// This function calculates No Freight pickup count
const getNoFreightpickupsCount = (stopsList) => {
  return stopsList.filter((stop) => stop.isNoFreight).length;
};

const isValidEmail = (email) => {
  return /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/.test(
    email
  );
};

const isValidPhone = (phone) => {
  return /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}$/.test(
    phone
  );
};

const intersectTwo = (array1, array2) => {
  return array1.filter((x) => {
    return array2.some((y) => {
      return Object.is(x, y);
    });
  });
};


// this method provides UTC datetime with respect to given offset value.
const getTerminalDateWithOffset = (dateString, offset) => {
  const date = new Date(dateString);
  const [hours, mins] = offset.split(":").map(Number);
  const totalOffsetMins = hours * 60 + (hours < 0 ? -1 : 1) * mins;
  return format(
    new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes() - totalOffsetMins,
      date.getUTCSeconds()
    ),
    appConstants.DATETYPES.DATETIME.updateFormat
  );
};



// this method provides the datetime in respective time zone using the given offset value.
const getTerminalDateByOffset = (dateString, offset) => {
  if(offset){
    const date = new Date(dateString);
    const [hours, mins] = offset.split(":").map(Number);
    const totalOffsetMins = hours * 60 + (hours < 0 ? -1 : 1) * mins;
    return format(
      new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes() + totalOffsetMins,
        date.getUTCSeconds()
      ),
      appConstants.DATETYPES.DATETIMEWITHOUTTIMEZONE.format
    );
  }
  return ""
};

const getDiffInMinutesWithTerminalTime = (date, terminalOffset) => {
  const origDate = new Date(date);
  const today = utcToZonedTime(new Date(), terminalOffset);
  const timeDiff = getTimeDifferenceInMinutes(
    new Date(0, 0, 0, origDate.getHours(), origDate.getMinutes()),
    new Date(0, 0, 0, today.getHours(), today.getMinutes())
  );
  return timeDiff;

  /* In this function we are comparing  date param which should be coming in terminal 
    timezone , to current time of the terminal timezone and returning the difference in minutes. 
  */
};

const getServiceWindow = (params) => {
  const { stopType, deliveryWindow, readyTime, closeTime } = params.data;
  if (stopType === appConstants.STOP_TYPE.Pickup) {
    return formatPickupServiceWindow(readyTime, closeTime);
  }
  return formatDeliveryOrServiceWindow(deliveryWindow);
};

const getFilteredDriverBreakDelays = (delays, delayType) => {
  return delays?.filter((delay) => delay.type === delayType) || [];
}

const getFilteredDriverBreakDelaysByDriverRoute = ({delays,delayType, driverRouteIds}) => {
  // filter delayed lunch stop only for currently dispatched drivers routes.
  // this function will filter out previously dispatched routes's delays 
  return (
    delays?.filter(
      (delay) =>
        delay.type === delayType &&
        driverRouteIds.includes(delay.routeId)
    ) || []
  );
};

const getTotalLunchTimeInMinutes = (lunchBreakStops, terminalOffset) => {
  const currentTime = utcToZonedTime(new Date(), terminalOffset)
  return lunchBreakStops.reduce((acc,delayStop)=>{
    const arrivalTime = delayStop.startTime;
    const deptTime = delayStop.endTime ?? format(currentTime,"HH:mm"); 
    const arrivalTimeInMinutes = getMinutesfromTimeString(arrivalTime);
    const departureTimeInMinutes = getMinutesfromTimeString(deptTime);
    const timeDiff = Math.max(departureTimeInMinutes - arrivalTimeInMinutes, 0);
    return acc + timeDiff;
  },0)
}

const getDailyAccruedHours = ({
  driverStartTime,
  lunchBreakStops,
  terminalOffset
}) => {
  if(!driverStartTime){
    return 0;
  }
  const currentTime = getUTCDate(new Date());
  const clockInTime = getUTCDate(driverStartTime);
  const currentAndClockedInTimeDiff = differenceInMinutes(
    currentTime,
    clockInTime
  );
  const totalLunchTime = getTotalLunchTimeInMinutes(lunchBreakStops, terminalOffset);
  const totalAccTimeInMinutes = currentAndClockedInTimeDiff - totalLunchTime;
  const dailyAccruedHours = parseFloat((totalAccTimeInMinutes / 60).toFixed(2));
  return dailyAccruedHours > 0 ? dailyAccruedHours: 0;
};

const getOverTimeHours = (actualHOS, allowedHOS) => {
  if(!allowedHOS){
    return 0;
  }
  return actualHOS - allowedHOS > 0
    ? parseFloat((actualHOS - allowedHOS).toFixed(2))
    : 0;
};

const getWeeklyAccruedHours = (dailyAccruedHours, accruedHours = 0) => {
  return parseFloat((accruedHours + dailyAccruedHours).toFixed(2));
};

const getAllCompletedStops = (stops) => {
  return sortStopsBySequence(stops).filter(stop => stop.isStopCompleted)
}

const getLastCompletedPickupStop = (stops) => {
  return getAllCompletedStops(stops).filter(
    (stop) => stop.stopType === appConstants.STOP_TYPE.Pickup
  ).at(-1);
}
const getUpdatedColumnHeaderName = ({ column, gridShortHeaderNamesConfig }) => {
  return {
    ...column,
    defaultHeaderName: column.headerName,
    ...(gridShortHeaderNamesConfig?.[column?.field] ?? {}),
  };
};

const updateGridColumnHeaderNames = ({
  gridShortHeaderNamesConfig,
  columnConfig,
  enableColumnShortForm,
}) => {
  if(!enableColumnShortForm){
    return columnConfig;
  }
  const columns = columnConfig.columns.map((column) => {
    if (column.children?.length) {
      return {
        ...column,
        children: column.children.map((childCol) =>
          getUpdatedColumnHeaderName({
            column: childCol,
            gridShortHeaderNamesConfig,
          })
        ),
      };
    }
    return getUpdatedColumnHeaderName({ column, gridShortHeaderNamesConfig });
  });
  return {
    ...columnConfig,
    columns,
  };
};

const getDriverDispatchTimeInUtcTime = (driverDispatchTime, terminalOffset) => {
  const driverDispatchTimeInUtcFormat = format(
    driverDispatchTime,
    appConstants.DATETYPES.DATETIME.updateFormat
  );
  return getTerminalDateWithOffset(
    driverDispatchTimeInUtcFormat,
    terminalOffset
  );
}

const getDriverRouteSPHBPHCount = ({
  driverRoutes,
  driverDispatchTime,
  terminalOffset,
  lunchBreakStops
}) => {
  if (driverRoutes.length === 0 || !driverDispatchTime) {
    return { stopsPerHour: 0, billsPerHour: 0 };
  }
  const driverDispatchTimeInUtcTime = getDriverDispatchTimeInUtcTime(
    driverDispatchTime,
    terminalOffset
  );
  //need to pass driverDispatchTime in same format as in driverClocked in time since both are in terminaltimezone
  if (
    !isDriverTimeBeforeCurrentDateAndTime(
      format(driverDispatchTime, appConstants.DATETYPES.DATETIME.updateFormat),
      terminalOffset
    )
  ) {
    return { stopsPerHour: 0, billsPerHour: 0 };
  }
  const kpiData = driverRoutes.reduce(
    (driverKpi, eachRoute) => {
      const routeKpi = eachRoute.stops.reduce(
        (routeKpi, eachStop) => {
          if (!eachStop.isStopCompleted) {
            return routeKpi;
          }
          routeKpi.completedStops += 1;
          routeKpi.completedBills += eachStop.shipments.length;
          return routeKpi;
        },
        { completedStops: 0, completedBills: 0 }
      );
      driverKpi.completedStops += routeKpi.completedStops;
      driverKpi.completedBills += routeKpi.completedBills;
      return driverKpi;
    },
    { completedStops: 0, completedBills: 0 }
  );

  const totalManHours = getTotalManHours({
    driverDispatchTimeInUtcTime,
    lunchBreakStops,
    terminalOffset
  });
  return {
    stopsPerHour: totalManHours ? (kpiData.completedStops / totalManHours).toFixed(2) : 0,
    billsPerHour: totalManHours ? (kpiData.completedBills / totalManHours).toFixed(2) : 0
  };
}

const getTotalManHours = ({
  driverDispatchTimeInUtcTime,
  lunchBreakStops,
  terminalOffset,
}) => {
  const currentTime = getUTCDate(new Date());
  const dispatchTime = getUTCDate(driverDispatchTimeInUtcTime);
  const currentAndDispatchTimeDiff = differenceInMinutes(
    currentTime,
    dispatchTime
  );

  const totalLunchTime = getTotalLunchTimeInMinutes(
    lunchBreakStops,
    terminalOffset
  );
  return parseFloat(
    ((currentAndDispatchTimeDiff - totalLunchTime) / 60).toFixed(2)
  );
};


async function getImagesFromBlobStorage(imageData) {
  const allDriversImages = imageData.map((urlData, index) => ({key: index, url: urlData.image }));
  const populatedImageData = [];
  return downloadBlobDataByURLs(allDriversImages)
    .then((blogImages) => {
      blogImages.forEach((image, index) => {
        populatedImageData.push({
          driverNumber: imageData[index].driverNumber,
          driverImage: `data:image/png;base64, ${image.blobData}`,
        });
      });
    })
    .then((_responses) => {
      return populatedImageData;
    })
    .catch((_error) => {
      return [];
    });
}

const fetchDriverImage = (driverNumbers, terminalCode) => {
  return getDataWithQueryString(
    "GetDriverImages",
    `terminalCode=${terminalCode}&driverNumbers=${JSON.stringify(
      driverNumbers
    )}`
  ).then(
    (response) => {
      if (response && response.status === 200 && response.data.length !== 0) {
        return getImagesFromBlobStorage(response.data);
      }
      return [];
    },
    (_error) => {
      return [];
    }
  );
};

const isShipmentsHasExcessiveLength = (shipments, stopType) => {
  return shipments?.some((shipment) => {
    if (stopType === appConstants.STOP_TYPE.Delivery) {
      return shipment.excessiveLength === appConstants.EXCESSIVE_SHIPMENT.YES;
    }
    return shipment.isExcessiveLength;
  });
};

const isStopHasFreightAmountShipments = (stopShipments) => {
  return stopShipments?.some((shipment) => shipment.freightAmount);
};
const isStopHasCodAmountShipments = (stopShipments) => {
  return stopShipments?.some((shipment) =>shipment.codAmount);
};

const isStopHasCodShipments = (stopShipments) => {
  return isStopHasFreightAmountShipments(stopShipments) || isStopHasCodAmountShipments(stopShipments);
};

const isDeliveryStopHasResunaPro = (shipments, stopType) => {
  if (stopType !== appConstants.STOP_TYPE.Delivery) {
    return false;
  }
  return shipments?.some((shipment) => shipment.isResuna);
};

const isDeliveryStopHasExhibitPro = (shipments, stopType) => {
  if (stopType !== appConstants.STOP_TYPE.Delivery) {
    return false;
  }
  return shipments?.some((shipment) => shipment.isExhibitPro);
};

const getDuplicatePickupsByPickupId = (duplicatePickups, pickupId) => {
  return duplicatePickups.find(
    (d) => d.pickupId.toLowerCase() === pickupId?.toLowerCase()
  )?.duplicatePickups ?? [];
};

const isPossibleMergeFilterApplied = (filterValue) => {
  return PICKUPS_GRID_FILTERS.possibleMerge === filterValue;
}

const getPickupStopStatus = ({
  stop,
  enableNoFreightCode = false,
  noFreightReasonCodeList,
  enableReturnExceptionReasonLabels = false,
}) => {
  const { PICKUP_STATUS } = appConstants;
  const baseStatus = stop.isNoFreight
    ? PICKUP_STATUS.NO_FREIGHT
    : PICKUP_STATUS.COMPLETED;

  if (stop.isNoFreight && enableNoFreightCode && stop.noFreightCode) {
    const label = getNoFreightStatusLabel(
      stop.noFreightCode,
      noFreightReasonCodeList,
      enableReturnExceptionReasonLabels
    );
    return `${baseStatus} (${label})`;
  }

  return baseStatus;
};

const getEquipmentSuffix = ({
  equipmentStatus,
  equipmentScac,
  showTrailerScac,
  prosLoadedInTrailer
}) => {
  const parts = [];
  if (showTrailerScac && equipmentScac) {
    parts.push(equipmentScac);
  }
  if (equipmentStatus) {
    parts.push(equipmentStatus);
  }
  if (prosLoadedInTrailer != null) {
    parts.push(String(prosLoadedInTrailer));
  }
  return parts.length ? parts.map(part => `(${part})`).join(' ') : '';
};

const getPickupTimeToCloseInMins = ({ closeTime, terminalOffset, closingSoonWarningTimeLimit }) => {
  const currentTerminalTimeInMins = getMinutesfromTimeString(
    format(utcToZonedTime(new Date(), terminalOffset), appConstants.DATETYPES.TIME.format)
  );
  // Calculate the time difference
  const closeTimeInMins = getMinutesfromTimeString(closeTime);
  const closingSoonTimeDiff = closeTimeInMins - currentTerminalTimeInMins;
  return closingSoonTimeDiff <= closingSoonWarningTimeLimit ? closingSoonTimeDiff : null;
};

const isActivePickup = (pickup,terminalOffset) => {
  return (
    isNotAssignedPickup({ pickup, terminalOffset }) ||
    isNotCompletedPickup({ pickup, terminalOffset })
  );
}

const getPickupClosingSoonText = (timeToClose) => {
  if (timeToClose === 0) {
    return 'Closed';
  }
  if (timeToClose > 0) {
    return `Closing in ${getMinutesInHourAndMinutesStr(timeToClose)}`;
  }
  return `Closed ${getMinutesInHourAndMinutesStr(Math.abs(timeToClose))} ago`;
}

const isCompletedOrFuturePickup = (pickup) => {
  return pickup.isCompleted || pickup.isFuture || pickup.isVoided || pickup.isMissed;
};

const checkIsStartTripEnabled = ({ startEndTripWorkflow, routeStatus }) => {
  return startEndTripWorkflow === CUSTOMER_CONFIG.RRTS
    ? routeStatus === ALL_DRIVER_STATUS.Dispatched || routeStatus === ALL_DRIVER_STATUS.PreDispatch
    : false;
 };
 
const isAllStopsCompleted = (driverRoutes) => {
 const totalStops = driverRoutes.reduce((acc, route) => {
   return [...acc, ...route.stops];
 }, []);
 return totalStops.every((stop) => stop.isStopCompleted);
}

const checkIsEndTripEnabled = ({ startEndTripWorkflow, routeStatus, driverRoutes }) => {
  return startEndTripWorkflow === CUSTOMER_CONFIG.RRTS
    ? (routeStatus !== ALL_DRIVER_STATUS.Dispatched && routeStatus !== ALL_DRIVER_STATUS.PreDispatch) && isAllStopsCompleted(driverRoutes)
    : routeStatus === ALL_DRIVER_STATUS.ArrivedAtTerminal;
};

const getCodIconTooltip = (isCodCharge, isFreightCharge) => {
  const tooltipArray = [];
  if (isCodCharge) {
    tooltipArray.push('COD');
  }
  if (isFreightCharge) {
    tooltipArray.push('Driver collect FRT charges');
  }
  return tooltipArray.join(", ");
};

const formatArriveAtTerminalTime = (arriveAtTerminalTime) => {
  return arriveAtTerminalTime
    ? format(arriveAtTerminalTime, appConstants.DATETYPES.TIME.format)
    : "";
}

const getPickupCountForPickupMarkerBadge = (pickup) => {
  const pickupNumberIdList = getPickupNumberIdListFromPickup(pickup);
  return pickupNumberIdList.length > 1 ? pickupNumberIdList.length : null;
};

const getShipmentStatusCodes = (shipment) => {
  if (shipment.returnCode) {
    return [shipment.returnCode];
  }
  if (shipment.exceptionCode?.length > 0) {
    return [...shipment.exceptionCode];
  }
  return [];
};

/**
 * Resolves a single return or exception code to its label using DispatchStore.
 * Returns label only (no fallback to code).
 */
const getStatusLabelForCode = (code, dispatchStore) => {
  if (!dispatchStore || !code) return null;
  const returnDetail = dispatchStore.getReturnDetails(code);
  if (returnDetail?.description) return returnDetail.description;
  const exceptionDetail = dispatchStore.getExceptionDetails(code);
  if (exceptionDetail?.description) return exceptionDetail.description;
  return null;
};

const getStatusDisplayValues = (statusCodes, dispatchStore, fallbackToCode = true) => {
  return statusCodes
    .map((code) => {
      const label = getStatusLabelForCode(code, dispatchStore);
      return label ?? (fallbackToCode ? code : null);
    })
    .filter(Boolean);
};

const formatShipmentsStatusCodesForTooltip = (
  shipments,
  dispatchStore,
  enableReturnExceptionReasonLabels = false
) => {
  if (!shipments?.length) {
    return null;
  }

  const useLabels = Boolean(dispatchStore) && enableReturnExceptionReasonLabels;
  let tooltipText;

  if (shipments.length === 1) {
    const statusCodes = getShipmentStatusCodes(shipments[0]);
    const displayValues = useLabels
      ? getStatusDisplayValues(statusCodes, dispatchStore, false)
      : statusCodes;
    tooltipText = displayValues.length > 0 ? displayValues.join(', ') : null;
  } else {
    const formattedItems = shipments
      .map((shipment) => {
        const statusCodes = getShipmentStatusCodes(shipment);
        const displayValues = useLabels
          ? getStatusDisplayValues(statusCodes, dispatchStore, false)
          : statusCodes;
        return displayValues.length > 0
          ? `${shipment.proId} (${displayValues.join(', ')})`
          : null;
      })
      .filter(Boolean);

    tooltipText = formattedItems.length > 0 ? formattedItems.join('\n') : null;
  }

  if (!tooltipText) {
    return null;
  }

  return <div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>;
};

/**
 * Resolves a no-freight reason code to its label using DispatchStore static client data.
 */
const getNoFreightStatusLabel = (noFreightCode, noFreightReasonCodeList, enableReturnExceptionReasonLabels) => {
  if (!noFreightCode) return null; // optional: only if every caller can handle null
  if (!enableReturnExceptionReasonLabels) {
    return noFreightCode;
  }
  const detail = Array.isArray(noFreightReasonCodeList)
    ? noFreightReasonCodeList.find((item) => item.code === noFreightCode)
    : null;
  return detail?.description ?? noFreightCode;
};

/**
 * Formats no-freight reason codes for select dropdown options.
 */
const getNoFreightCodeOptionLabel = ({
  description,
  code,
  enableReturnExceptionReasonLabels = false
}) => {
  if (enableReturnExceptionReasonLabels) {
    return description ?? code;
  }
  return code;
};

const belongsToTerminal = (pickup, terminalCode) => {
  const normalizedTerminalCode = terminalCode?.toUpperCase();
  if (!normalizedTerminalCode || !pickup?.terminalCode) {
    return true;
  }
  return pickup.terminalCode.toUpperCase() === normalizedTerminalCode;
};
 
const filterPickupsForTerminalUpdate = (
  pickups,
  pickupIdsToRemove,
  terminalCode
) => {
  const pickupIds = Array.isArray(pickupIdsToRemove)
    ? pickupIdsToRemove
    : [pickupIdsToRemove];
  const normalizedPickupIdsToRemove = new Set(
    pickupIds.filter(Boolean).map((pickupId) => pickupId.toUpperCase())
  );

  return pickups.filter((pickup) => {
    if (normalizedPickupIdsToRemove.has(pickup.pickupId?.toUpperCase())) {
      return false;
    }
    if (terminalCode && !belongsToTerminal(pickup, terminalCode)) {
      return false;
    }
    return true;
  });
};

/* Returns the matching service level entries so callers can pick the mapped value for
 comparison and the display value for labels. */
const filterServiceLevelsByMode = (serviceLevelList, modes) => {
  return (
    serviceLevelList?.filter(
      (data) => data.isVisible && modes.includes(data.mode)
    ) || []
  );
}

export {
  formatPhone,
  formatAddress,
  formatNumber,
  isDeliveryStop,
  getFormattedDeliveryWindow,
  getPendingStopStatus,
  getServiceLevel,
  getServiceWindow,
  formatDeliveryOrServiceWindow,
  checkIfETAInDeliveryRange,
  getDeliveryStatusFromShipmentDelCode,
  formatPickupServiceWindow,
  sortStopsBySequence,
  getCurrentDriverStatus,
  isDriverCurrentStopPresent,
  completedVsTotalStopsFormatter,
  getTotalInTrailer,
  getDispatchRouteColor,
  getTotalReturnDeliveryShipments,
  getTimeDifferenceInMinutes,
  convertMinutesintoHours,
  formatElapsedSecondsForDisplay,
  getTotalDeliveryShipments,
  getPendingDeliveryShipments,
  getCompletedDeliveryShipments,
  getTotalCompletedDeliveryStops,
  getTotalDeliveryStops,
  getNoFreightpickupsCount,
  getPendingStops,
  isValidEmail,
  isValidPhone,
  getStopServiceWindowByStopType,
  intersectTwo,
  getTerminalDateWithOffset,
  getTerminalDateByOffset,
  getCurrentStop,
  getDiffInMinutesWithTerminalTime,
  getDailyAccruedHours,
  getOverTimeHours,
  getWeeklyAccruedHours,
  getLastCompletedPickupStop,
  updateGridColumnHeaderNames,
  getAllCompletedStops,
  getDriverRouteSPHBPHCount,
  fetchDriverImage,
  getFilteredDriverBreakDelays,
  isShipmentsHasExcessiveLength,
  isDeliveryStopHasResunaPro,
  isDeliveryStopHasExhibitPro,
  getDuplicatePickupsByPickupId,
  isPossibleMergeFilterApplied,
  getFilteredDriverBreakDelaysByDriverRoute,
  getPickupStopStatus,
  getEquipmentSuffix,
  getPickupTimeToCloseInMins,
  getPickupClosingSoonText,
  isActivePickup,
  isCompletedOrFuturePickup,
  isStopHasCodShipments,
  checkIsStartTripEnabled,
  checkIsEndTripEnabled,
  getAptComments,
  isStopHasFreightAmountShipments,
  isStopHasCodAmountShipments,
  getCodIconTooltip,
  getStopDeliveryStatusFromShipmentsDelCode,
  formatArriveAtTerminalTime,
  normalizeDeliveryWindow,
  getPickupCountForPickupMarkerBadge,
  getShipmentStatusCodes,
  getStatusLabelForCode,
  getStatusDisplayValues,
  formatShipmentsStatusCodesForTooltip,
  getNoFreightStatusLabel,
  getNoFreightCodeOptionLabel,
  belongsToTerminal,
  filterPickupsForTerminalUpdate,
  filterServiceLevelsByMode
};
